import { useMemo, useState } from "react";
import ShowCard from "../components/ShowCard";
import { useAllShows } from "../hooks/useShows";
import { lower } from "../lib/strings";

// ---------- helpers ----------
const hasCat = (s, cat) =>
  (s.categories || []).some((c) => lower(c) === cat);

const matchesAny = (s, cats) => cats.some((c) => hasCat(s, c));

const matchesQuery = (s, q) => {
  if (!q) return true;
  const needle = q.toLowerCase().trim();
  if (!needle) return true;
  const haystack = [
    s.title,
    s.theatre,
    s.venue,
    s.company,
    s.playwright,
    s.creator,
    s.director,
    ...((s.cast || [])),
  ]
    .filter(Boolean)
    .map((v) => String(v).toLowerCase())
    .join(" || ");
  return haystack.includes(needle);
};

export default function Toronto() {
  const allShows = useAllShows();
  const [active, setActive] = useState("now-playing");
  const [query, setQuery] = useState("");

  // city-scoped (toronto)
  const torontoShows = useMemo(
    () => allShows.filter((s) => lower(s.city || "toronto") === "toronto"),
    [allShows]
  );

  const FILTERS = useMemo(() => {
    const now = Date.now();
    const TWO_WEEKS = 14 * 24 * 60 * 60 * 1000;
    return [
      // ---- temporal (computed from startDate/endDate via backend status) ----
      {
        id: "now-playing",
        label: "now playing",
        match: (s) => s.status === "now playing" || s.status === "current",
      },
      {
        id: "closing-soon",
        label: "closing soon",
        match: (s) => {
          if (!s.endDate) return false;
          if (s.status !== "now playing" && s.status !== "current") return false;
          const end = Date.parse(s.endDate);
          if (Number.isNaN(end)) return false;
          return end - now <= TWO_WEEKS;
        },
      },
      {
        id: "upcoming",
        label: "upcoming",
        match: (s) => s.status === "upcoming",
      },
      {
        id: "closed",
        label: "closed",
        match: (s) => s.status === "closed",
      },
      // ---- category (descriptive tags) ----
      { id: "theatre", label: "theatre", match: (s) => hasCat(s, "theatre") },
      { id: "musical", label: "musical", match: (s) => hasCat(s, "musical") },
      { id: "opera", label: "opera", match: (s) => hasCat(s, "opera") },
      { id: "dance", label: "dance", match: (s) => hasCat(s, "dance") },
      {
        id: "comedy-improv",
        label: "comedy / improv",
        match: (s) => matchesAny(s, ["comedy", "improv"]),
      },
      {
        id: "family",
        label: "family",
        match: (s) => matchesAny(s, ["family", "young audiences"]),
      },
      { id: "festival", label: "festival", match: (s) => hasCat(s, "festival") },
      { id: "indie", label: "indie", match: (s) => hasCat(s, "indie") },
      {
        id: "commercial",
        label: "commercial",
        match: (s) => hasCat(s, "commercial") || matchesAny(s, ["big commercial"]),
      },
      {
        id: "interdisciplinary",
        label: "interdisciplinary",
        match: (s) => hasCat(s, "interdisciplinary"),
      },
    ];
  }, []);

  const filterCounts = useMemo(() => {
    const counts = {};
    FILTERS.forEach((f) => {
      counts[f.id] = torontoShows.filter(f.match).length;
    });
    return counts;
  }, [FILTERS, torontoShows]);

  // when search is active, results are searched across the FULL toronto
  // catalogue (ignoring filter); filter applies only when search is empty.
  const filtered = useMemo(() => {
    const q = query.trim();
    if (q) {
      return torontoShows.filter((s) => matchesQuery(s, q));
    }
    const f = FILTERS.find((x) => x.id === active);
    if (!f) return torontoShows;
    return torontoShows.filter(f.match);
  }, [active, FILTERS, torontoShows, query]);

  return (
    <main data-testid="toronto-page" className="min-h-screen bg-black text-white pb-28">
      <header className="px-5 pt-6 pb-4 border-b-2 border-white/15">
        <div className="flex items-baseline justify-between">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">
              city · 01
            </div>
            <h1 className="font-display text-5xl sm:text-6xl leading-none mt-1">
              toronto<span className="text-acid">.</span>
            </h1>
            <div className="font-mono text-xs text-white/50 mt-2">
              {torontoShows.length} shows · live performance
            </div>
          </div>
        </div>
      </header>

      {/* test task callout */}
      <section
        data-testid="test-task-callout"
        className="mx-5 mt-5 border-2 border-acid p-4 bg-acid/5"
      >
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-acid mb-2">
          your task
        </div>
        <p className="font-display text-xl leading-tight text-white">
          save one show you'd see, or log one you've already seen.
        </p>
      </section>

      {/* search */}
      <div className="px-5 mt-5">
        <input
          data-testid="toronto-search"
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="search title, company, venue, cast…"
          className="w-full bg-transparent border-2 border-white/20 text-white font-mono text-sm placeholder-white/30 px-3 py-3 focus:border-acid focus:outline-none"
        />
        {query.trim() && (
          <div
            data-testid="toronto-search-meta"
            className="mt-2 font-mono text-[11px] uppercase tracking-wider text-white/50"
          >
            search · {filtered.length} match{filtered.length === 1 ? "" : "es"} across full catalogue
          </div>
        )}
      </div>

      {/* filter tabs */}
      {!query.trim() && (
        <nav data-testid="toronto-filters" className="mt-5 border-y border-white/15">
          <div className="overflow-x-auto no-scrollbar">
            <ul className="flex whitespace-nowrap">
              {FILTERS.map((f) => {
                const isActive = f.id === active;
                return (
                  <li key={f.id}>
                    <button
                      data-testid={`filter-${f.id}`}
                      onClick={() => setActive(f.id)}
                      className={`px-4 py-3 font-mono text-[11px] uppercase tracking-wider border-r border-white/15 transition-colors ${
                        isActive
                          ? "bg-acid text-black"
                          : "text-white/70 hover:text-white hover:bg-white/5"
                      }`}
                    >
                      {f.label}{" "}
                      <span className={`tabular-nums ${isActive ? "text-black/70" : "text-white/40"}`}>
                        ({filterCounts[f.id]})
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        </nav>
      )}

      <section data-testid="toronto-list">
        {filtered.length === 0 ? (
          <div className="px-5 py-16 text-center font-mono text-sm text-white/40">
            nothing here yet.
          </div>
        ) : (
          <ul>
            {filtered.map((s, i) => (
              <li key={s.id}>
                <ShowCard show={s} index={i} />
              </li>
            ))}
          </ul>
        )}
      </section>
    </main>
  );
}
